<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  endOfDay,
  endOfWeek,
  endOfMonth,
  format,
  isAfter,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns'

const props = defineProps<{ period: Date[] }>()

const emit = defineEmits<{
  (e: 'change-date', period: Date[]): void
}>()

const open = ref(false)
const customStart = ref('')
const customEnd = ref('')
const customError = ref('')

const now = () => new Date()

interface Preset {
  key: string
  label: string
  range: () => Date[]
}

// Presets definem o horário automaticamente (início no começo do dia, fim "agora"
// ou no fim do período) — elimina a necessidade de ajustar horário manualmente.
const presets: Preset[] = [
  { key: 'today', label: 'Hoje', range: () => [startOfDay(now()), now()] },
  { key: '7d', label: 'Últimos 7 dias', range: () => [startOfDay(subDays(now(), 6)), now()] },
  { key: '15d', label: 'Últimos 15 dias', range: () => [startOfDay(subDays(now(), 14)), now()] },
  { key: '30d', label: 'Últimos 30 dias', range: () => [startOfDay(subDays(now(), 29)), now()] },
  { key: 'thisWeek', label: 'Semana atual', range: () => [startOfWeek(now(), { weekStartsOn: 1 }), now()] },
  {
    key: 'lastWeek',
    label: 'Semana anterior',
    range: () => {
      const ref = subWeeks(now(), 1)
      return [startOfWeek(ref, { weekStartsOn: 1 }), endOfWeek(ref, { weekStartsOn: 1 })]
    },
  },
  { key: 'thisMonth', label: 'Mês atual', range: () => [startOfMonth(now()), now()] },
  {
    key: 'lastMonth',
    label: 'Mês anterior',
    range: () => {
      const ref = subMonths(now(), 1)
      return [startOfMonth(ref), endOfMonth(ref)]
    },
  },
  { key: '4months', label: 'Últimos 4 meses', range: () => [startOfDay(subMonths(now(), 4)), now()] },
]

const label = computed(() => {
  if (props.period.length < 2) return 'Selecione o período'
  return `${format(props.period[0], 'dd/MM/yyyy HH:mm')} – ${format(props.period[1], 'dd/MM/yyyy HH:mm')}`
})

function toInputDate(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

watch(open, (isOpen) => {
  if (!isOpen) return
  customError.value = ''
  if (props.period.length === 2) {
    customStart.value = toInputDate(props.period[0])
    customEnd.value = toInputDate(props.period[1])
  }
})

function applyPreset(preset: Preset) {
  emit('change-date', preset.range())
  open.value = false
}

function applyCustom() {
  if (!customStart.value || !customEnd.value) {
    customError.value = 'Informe as duas datas.'
    return
  }
  const start = startOfDay(new Date(`${customStart.value}T00:00:00`))
  let end = endOfDay(new Date(`${customEnd.value}T00:00:00`))
  if (isAfter(start, end)) {
    customError.value = 'A data inicial deve ser anterior à final.'
    return
  }
  // Não permite período no futuro além de agora.
  if (isAfter(end, now())) end = now()
  customError.value = ''
  emit('change-date', [start, end])
  open.value = false
}
</script>

<template>
  <div class="relative">
    <button
      type="button"
      class="flex min-w-60 items-center justify-between gap-3 border border-zinc-300 bg-white px-4 py-2.5 text-left text-sm transition hover:border-primary"
      @click="open = true">
      <span class="truncate" :class="period.length === 2 ? 'text-slate-700' : 'text-slate-400'">{{ label }}</span>
      <svg class="h-4 w-4 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </button>

    <div v-if="open" class="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40" @click="open = false" />
      <div class="relative w-full max-w-2xl bg-white p-6 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h3 class="text-lg font-semibold text-slate-900">Selecionar período</h3>
          <button type="button" class="text-slate-400 hover:text-slate-700" @click="open = false">✕</button>
        </div>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <!-- Presets -->
          <div>
            <p class="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Atalhos</p>
            <div class="grid grid-cols-2 gap-2">
              <button
                v-for="preset in presets"
                :key="preset.key"
                type="button"
                class="border border-stroke px-3 py-2 text-left text-sm text-slate-700 transition hover:border-primary hover:bg-primary/5"
                @click="applyPreset(preset)">
                {{ preset.label }}
              </button>
            </div>
          </div>

          <!-- Personalizado -->
          <div>
            <p class="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Período personalizado</p>
            <div class="space-y-3">
              <div>
                <label class="mb-1 block text-sm text-slate-600">Data inicial</label>
                <input
                  v-model="customStart"
                  type="date"
                  class="w-full border border-zinc-300 px-3 py-2 focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label class="mb-1 block text-sm text-slate-600">Data final</label>
                <input
                  v-model="customEnd"
                  type="date"
                  class="w-full border border-zinc-300 px-3 py-2 focus:border-primary focus:outline-none" />
              </div>
              <p class="text-xs text-slate-400">
                O horário é definido automaticamente: início às 00:00 e fim no final do dia.
              </p>
              <p v-if="customError" class="text-sm text-red">{{ customError }}</p>
              <button
                type="button"
                class="w-full bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
                @click="applyCustom">
                Aplicar período personalizado
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
